// frontend/src/components/loi/useLoiOverrides.ts
/**
 * His changes to the open letter (spec docs/planning/2026-09-29-loi-override-design.md, 3.6-3.7), held in the
 * dialog until Generate. ⛔ A draft is PENDING until he accepts it; exact words, remove and undo need no AI.
 */
import { useCallback, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { newUuid } from '../../lib/uuid';
import type { LoiGenerateInput } from '../../types';
import type { DraftedChange, LoiPreview, LoiSectionKey, SectionOverride } from '../../types/loiOverride';

export interface PendingChange {
  pendingId: string;
  instruction: string;
  target: string | null;
  source: 'chat' | 'document';
  change: DraftedChange;
}

/**
 * A draft that brought no change to look at - the AI answered with none, or the draft was refused. ⛔ KEPT (like
 * `refused`, Ruling 25): the chat drafts its asks one after another and has no box to reopen, so a refusal held in one
 * slot was wiped when the next ask started.
 */
export interface DraftMiss {
  /** His words, as he asked. */
  instruction: string;
  /** The section asked about - shown by its title, never its key; null for an ask about the whole letter. */
  target: string | null;
  /** null: the AI answered with no change. Otherwise why it was not drafted, in the server's words. */
  refusal: string | null;
}

export interface LoiOverridesState {
  overrides: SectionOverride[];
  skip: string[];
  preview: LoiPreview | null;
  pending: PendingChange[];
  refused: string[];
  misses: DraftMiss[];
  busy: 'preview' | 'draft' | null;
  error: string | null;
  stale: boolean;
  refresh: (next?: { overrides?: SectionOverride[]; skip?: string[] }) => Promise<void>;
  /** Resolves true when it left at least one proposed change waiting for his Accept - false on no change or a refusal. */
  draft: (instruction: string, target: string | null, source: 'chat' | 'document') => Promise<boolean>;
  accept: (pendingId: string) => Promise<void>;
  discard: (pendingId: string) => void;
  exactWords: (section: string, words: string, title?: string) => Promise<void>;
  removeSection: (section: string, title: string) => Promise<void>;
  undo: (overrideId: string) => Promise<void>;
  setSkip: (ruleId: string, skipped: boolean) => Promise<void>;
  markStale: () => void;
  reset: () => void;
}

const message = (e: unknown, fallback: string): string => (e instanceof Error && e.message ? e.message : fallback);
/** The most one change's `asked` holds - the server's cap (loiOverrides.ts MAX_ASKED_CHARS). */
const MAX_ASKED_CHARS = 4000;
/**
 * ⛔ Ruling 31: why his words cannot be one change, in words - null when they fit. ONE rule for this hook's refusal and
 *    the Change box, which keeps refused words: if two copies drifted apart, the box could close on words the hook
 *    refused, and his words would go with it. Every character he gave counts (never trimmed), as the server counts `asked`.
 * ⛔ It never says "make it two changes": a second change to the SAME section replaces the first (both report applied),
 *    so following that advice on the section he is editing silently dropped the first part of his clause (111th review).
 */
export const tooLongForOneChange = (words: string): string | null =>
  words.length > MAX_ASKED_CHARS
    ? `Your words run to ${words.length.toLocaleString('en-US')} characters - one change holds at most ${MAX_ASKED_CHARS.toLocaleString('en-US')}. Say it in fewer words.`
    : null;
const paragraphsOf = (words: string): string[] => words.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

const nextCustomKey = (preview: LoiPreview | null, overrides: SectionOverride[]): `custom_${number}` => {
  const used = [...(preview?.sections ?? []).map((s) => s.key), ...overrides.map((o) => o.newKey ?? '')]
    .map((k) => /^custom_(\d+)$/.exec(k)?.[1])
    .filter((n): n is string => !!n)
    .map(Number);
  return `custom_${(used.length ? Math.max(...used) : 0) + 1}`;
};

export function useLoiOverrides(readInput: () => LoiGenerateInput | null): LoiOverridesState {
  const [overrides, setOverrides] = useState<SectionOverride[]>([]);
  const [skip, setSkipList] = useState<string[]>([]);
  const [preview, setPreview] = useState<LoiPreview | null>(null);
  const [pending, setPending] = useState<PendingChange[]>([]);
  const [refused, setRefused] = useState<string[]>([]);
  const [misses, setMisses] = useState<DraftMiss[]>([]);
  const [busy, setBusy] = useState<'preview' | 'draft' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  // The latest lists, for calls made before React re-renders (accept -> refresh in one tick).
  const latest = useRef({ overrides, skip, preview });
  latest.current = { overrides, skip, preview };
  /**
   * ⛔ A LATE ANSWER NEVER LANDS ON THE WRONG LETTER. `letterNo` moves on reset - the dialog closed, so a draft asked
   *   for the last letter must not arrive in the next one (a change drafted against another deal's facts). `previewNo`
   *   moves on every preview - an older preview answering after a newer one would show a letter his changes no longer
   *   make. Each call remembers the numbers it started under and keeps its answer only if they still stand.
   */
  const letterNo = useRef(0);
  const previewNo = useRef(0);
  /**
   * ⛔ Ruling 26c: EVERY edit to the form counts here, preview or no preview. A preview remembers the count it read the
   *   form at, so its answer knows whether he edited while it was out - the first preview's flight included.
   */
  const editNo = useRef(0);
  /**
   * ⛔ Ruling 28a: what is OUT, so `busy` names it truly - a draft outranks a preview, and busy clears only when nothing
   *   is out. (A preview landing mid-draft cleared it: "Drafting your change..." vanished and "Draft it" came back - a
   *   second PAID draft.) Counted for THIS letter: `reset` zeroes it, and a call from the last letter never touches it.
   */
  const out = useRef({ drafts: 0, preview: false });
  const showBusy = useCallback(() => {
    setBusy(out.current.drafts > 0 ? 'draft' : out.current.preview ? 'preview' : null);
  }, []);

  const refresh = useCallback(async (next?: { overrides?: SectionOverride[]; skip?: string[] }) => {
    const input = readInput();
    if (!input) return;
    const readAt = editNo.current;
    const o = next?.overrides ?? latest.current.overrides;
    const s = next?.skip ?? latest.current.skip;
    const mine = ++previewNo.current;
    const forLetter = letterNo.current;
    const stillMine = () => mine === previewNo.current && forLetter === letterNo.current;
    out.current.preview = true;
    showBusy();
    setError(null);
    try {
      const res = await api.previewLoi({ ...input, sectionOverrides: o }, s);
      if (!stillMine()) return;
      setPreview(res.preview);
      setStale(editNo.current !== readAt);
    } catch (e) {
      if (stillMine()) setError(message(e, 'The letter could not be previewed.'));
    } finally {
      if (stillMine()) { out.current.preview = false; showBusy(); }
    }
  }, [readInput, showBusy]);

  const commit = useCallback(async (next: SectionOverride[]) => {
    setOverrides(next);
    latest.current = { ...latest.current, overrides: next };
    await refresh({ overrides: next });
  }, [refresh]);

  const make = (o: Omit<SectionOverride, 'id' | 'flags' | 'createdBy' | 'createdAt'> & { flags?: string[] }): SectionOverride => ({
    id: newUuid(),
    flags: [],
    createdBy: '',
    createdAt: new Date().toISOString(),
    ...o,
  });

  const draft = useCallback(async (instruction: string, target: string | null, source: 'chat' | 'document'): Promise<boolean> => {
    const input = readInput();
    const asked = instruction.trim();
    if (!input || !asked) return false;
    const forLetter = letterNo.current;
    const sameAsk = (m: DraftMiss) => m.instruction === asked && m.target === target;
    // ⛔⛔ I2: a draft that brings nothing is SAID and KEPT - his words, the section, and why - never lost in silence.
    const missed = (refusal: string | null) =>
      setMisses((all) => [...all.filter((m) => !sameAsk(m)), { instruction: asked, target, refusal }]);
    out.current.drafts += 1;
    showBusy();
    // The same words asked again: the line about the last try gives way to this one's answer. ⛔ `error` is left
    // alone - it is the PREVIEW's; a draft's refusal is its own line, so the next draft cannot wipe it.
    setMisses((all) => all.filter((m) => !sameAsk(m)));
    try {
      // ⛔ Ruling 13: the draft reads the SAME letter the preview shows - his house style applied, his skips skipped.
      // D9: the preview's keys are the backend's own section keys - the request names one as the type it is.
      const res = await api.draftLoiChange(
        { ...input, sectionOverrides: latest.current.overrides }, asked, target as LoiSectionKey | null, latest.current.skip,
      );
      if (forLetter !== letterNo.current) return false;
      setPending((p) => [
        ...p,
        ...res.draft.changes.map((change) => ({ pendingId: newUuid(), instruction: asked, target, source, change })),
      ]);
      // ⛔ Ruling 25: KEPT, not replaced - the chat's asks draft one after another, and a new draft must not hide what an
      //   earlier ask could not use. Each line once; `reset` clears them with the letter.
      setRefused((r) => Array.from(new Set([...r, ...res.draft.refused])));
      // "No changes" is an answer, and silence would lose his words.
      if (res.draft.changes.length === 0) missed(null);
      return res.draft.changes.length > 0;
    } catch (e) {
      if (forLetter === letterNo.current) missed(message(e, 'That change could not be drafted.'));
      return false;
    } finally {
      if (forLetter === letterNo.current) { out.current.drafts -= 1; showBusy(); }
    }
  }, [readInput, showBusy]);

  const accept = useCallback(async (pendingId: string) => {
    const p = pending.find((x) => x.pendingId === pendingId);
    if (!p) return;
    const c = p.change;
    const o = make({
      section: c.section,
      action: c.action,
      ...(c.action === 'add_after' ? { newKey: nextCustomKey(latest.current.preview, latest.current.overrides), title: c.title ?? 'Added' } : {}),
      ...(c.action === 'rewrite' && c.title ? { title: c.title } : {}),
      ...(c.action !== 'remove' ? { paragraphs: c.paragraphs } : {}),
      asked: p.instruction,
      source: p.source,
      flags: c.flags,
    });
    setPending((all) => all.filter((x) => x.pendingId !== pendingId));
    await commit([...latest.current.overrides, o]);
  }, [pending, commit]);

  const exactWords = useCallback(async (section: string, words: string, title?: string) => {
    // ⛔ D5: his words are the change's `asked` too, and one change holds 4,000 characters - said here with the real
    //   limit, never sent to a server 400 that reads "does not say what was asked".
    const tooLong = tooLongForOneChange(words);
    if (tooLong) {
      setError(tooLong);
      return;
    }
    const paragraphs = paragraphsOf(words);
    if (paragraphs.length === 0) return;
    const o = title
      ? make({ section: section as SectionOverride['section'], action: 'add_after', newKey: nextCustomKey(latest.current.preview, latest.current.overrides), title, paragraphs, asked: words, source: 'document' })
      : make({ section: section as SectionOverride['section'], action: 'verbatim', paragraphs, asked: words, source: 'document' });
    await commit([...latest.current.overrides, o]);
  }, [commit]);

  const removeSection = useCallback(async (section: string, title: string) => {
    await commit([...latest.current.overrides, make({ section: section as SectionOverride['section'], action: 'remove', asked: `Remove the ${title} section`, source: 'document' })]);
  }, [commit]);

  const undo = useCallback(async (overrideId: string) => {
    await commit(latest.current.overrides.filter((o) => o.id !== overrideId));
  }, [commit]);

  const setSkip = useCallback(async (ruleId: string, skipped: boolean) => {
    const next = skipped ? [...latest.current.skip, ruleId] : latest.current.skip.filter((id) => id !== ruleId);
    setSkipList(next);
    latest.current = { ...latest.current, skip: next };
    await refresh({ skip: next });
  }, [refresh]);

  return {
    overrides, skip, preview, pending, refused, misses, busy, error, stale,
    refresh,
    draft,
    accept,
    discard: (pendingId: string) => setPending((all) => all.filter((x) => x.pendingId !== pendingId)),
    exactWords,
    removeSection,
    undo,
    setSkip,
    // Every edit counts (Ruling 26c), preview or not - with no preview on screen the panel simply shows no notice.
    markStale: () => { editNo.current += 1; setStale(true); },
    reset: () => {
      letterNo.current += 1;
      out.current = { drafts: 0, preview: false };
      latest.current = { overrides: [], skip: [], preview: null };
      setOverrides([]); setSkipList([]); setPreview(null); setPending([]); setRefused([]); setMisses([]); setBusy(null); setError(null); setStale(false);
    },
  };
}
